// Coach-mark step definitions per role.
//
// Each step anchors to a DOM element via a [data-coach="..."]
// attribute. Steps are designed for the *bottom navigation bar*
// (always visible) and a couple of high-value primary actions so
// the user learns where to TAP for each task — the modal product
// tour already covered the WHAT, this is the WHERE.
//
// Step shape:
//   {
//     target:    CSS selector — '[data-coach="..."]'
//     title:     short noun phrase
//     body:      1-2 sentence description (under 120 chars)
//     placement: 'top' | 'bottom' | 'auto' (default auto)
//     accent?:   per-step color override (h.* theme token name)
//   }
//
// `accent` is looked up from the theme at render time (we can't
// embed h.gn here directly because themes are picked at runtime).

const COMMON = {
  notif: '[data-coach="notif-bell"]',
  settings: '[data-coach="settings-gear"]',
};

export function getLandlordCoachSteps() {
  return [
    {
      target: '[data-coach="nav-dash"]',
      title: "Your Dashboard",
      body: "Quick stats live here — rent paid this month, pending tenants, recent activity.",
      accent: "ac",
    },
    {
      target: '[data-coach="nav-props"]',
      title: "Properties Tab",
      body: "Tap here to add a new property or edit photos, rent, and amenities for existing ones.",
      accent: "gn",
    },
    {
      target: '[data-coach="nav-tens"]',
      title: "Tenants Tab",
      body: "Invite tenants via WhatsApp, view their lease details, and see payment history per tenant.",
      accent: "pp",
    },
    {
      target: '[data-coach="nav-pay"]',
      title: "Payments Tab",
      body: "Track who paid, who's pending, and accept online payments. Filter by month or status.",
      accent: "wn",
    },
    {
      target: '[data-coach="nav-maint"]',
      title: "Maintenance Tab",
      body: "Tenant requests show up here. Assign a service person and track jobs from start to finish.",
      accent: "ac",
    },
    {
      target: COMMON.settings,
      title: "Settings & Plan",
      body: "Tap the gear icon for your plan, billing history, reports, dark mode, and your account.",
      accent: "gn",
      placement: "bottom",
    },
  ];
}

export function getTenantCoachSteps() {
  return [
    {
      target: '[data-coach="nav-dash"]',
      title: "My House",
      body: "Your home base — current rent, upcoming bills, recent activity from your landlord.",
      accent: "ac",
    },
    {
      target: '[data-coach="nav-mapview"]',
      title: "Map View",
      body: "Browse other properties listed by landlords in your area, in case you're house-hunting.",
      accent: "gn",
    },
    {
      target: '[data-coach="nav-pay"]',
      title: "My Payments",
      body: "Pay rent in 3 taps via UPI, cards, or net banking. Receipts download automatically.",
      accent: "pp",
    },
    {
      target: '[data-coach="nav-maint"]',
      title: "Maintenance",
      body: "Report issues with photos. Your landlord gets a notification instantly.",
      accent: "wn",
    },
    {
      target: '[data-coach="nav-chat"]',
      title: "Chat",
      body: "Direct chat with your landlord — no SMS fees, no phone-tag.",
      accent: "ac",
    },
    {
      target: COMMON.settings,
      title: "Profile & Settings",
      body: "Tap the gear for your profile, agreement, language, and dark mode preferences.",
      accent: "gn",
      placement: "bottom",
    },
  ];
}

export function getServicePersonCoachSteps() {
  return [
    {
      target: '[data-coach="nav-dash"]',
      title: "Service Dashboard",
      body: "At-a-glance view of active jobs, completed jobs, and your earnings this month.",
      accent: "ac",
    },
    {
      target: '[data-coach="nav-jobs"]',
      title: "My Jobs",
      body: "Active and assigned jobs from landlords. Tap any job to update status or add notes.",
      accent: "gn",
    },
    {
      target: '[data-coach="nav-mapview"]',
      title: "Map View",
      body: "Find nearby properties — useful for routing your day across multiple jobs.",
      accent: "pp",
    },
    {
      target: '[data-coach="nav-history"]',
      title: "Job History",
      body: "Every job you've completed, searchable and exportable. Useful for reviews and taxes.",
      accent: "wn",
    },
    {
      target: '[data-coach="nav-earnings"]',
      title: "Earnings",
      body: "Track payouts from landlords. Filter by month, see which jobs are paid vs pending.",
      accent: "ac",
    },
    {
      target: COMMON.settings,
      title: "Profile & Settings",
      body: "Tap the gear for your profile, skill set, language, and dark mode.",
      accent: "gn",
      placement: "bottom",
    },
  ];
}

export default function coachStepsForRole(role) {
  if (role === "landlord") return getLandlordCoachSteps();
  if (role === "tenant") return getTenantCoachSteps();
  if (role === "service") return getServicePersonCoachSteps();
  return [];
}
